import type {
  OfflineCreateCommentIntent,
  OfflineCreateDraftIntent,
  OfflineQueueActor,
  OfflineQueueRecord,
  OfflineSubmitDraftIntent,
  OfflineUpdateDraftIntent,
  RequisitionCommentCreatePayload,
  RequisitionCreatePayload,
} from "../types/requisition";
import { appendQueuedIntent, listQueuedIntentsByStatus } from "./indexedDB/queueStore";
import { allocateNextEventSequence } from "./indexedDB/syncMetadata";

interface CreateDraftIntentInput extends OfflineQueueActor {
  localDraftId: string;
  payload: RequisitionCreatePayload;
}

interface UpdateDraftIntentInput extends OfflineQueueActor {
  localDraftId: string;
  payload: Partial<RequisitionCreatePayload>;
}

interface SubmitDraftIntentInput extends OfflineQueueActor {
  localDraftId: string;
}

interface CreateCommentIntentInput extends OfflineQueueActor {
  localDraftId: string;
  serverRequisitionId: string;
  payload: RequisitionCommentCreatePayload & {
    client_comment_id: string;
  };
}

function buildIntentId(localDraftId: string, eventSequence: number): string {
  return `${localDraftId}:${eventSequence}`;
}

async function createQueuedIntent<TIntent extends OfflineQueueRecord>(
  record: Omit<TIntent, "id" | "eventSequence" | "status" | "createdAt" | "updatedAt" | "lastError">,
): Promise<TIntent> {
  const eventSequence = await allocateNextEventSequence();
  const timestamp = new Date().toISOString();

  const queueRecord = {
    ...record,
    id: buildIntentId(record.localDraftId, eventSequence),
    eventSequence,
    status: "pending" as const,
    createdAt: timestamp,
    updatedAt: timestamp,
    lastError: null,
  } as TIntent;

  return appendQueuedIntent(queueRecord) as Promise<TIntent>;
}

export async function enqueueCreateDraftIntent(input: CreateDraftIntentInput): Promise<OfflineCreateDraftIntent> {
  return createQueuedIntent<OfflineCreateDraftIntent>({
    entityType: "requisition",
    intentType: "create_draft",
    localDraftId: input.localDraftId,
    serverRequisitionId: null,
    vesselId: input.vesselId,
    authorUserId: input.authorUserId,
    payload: input.payload,
  });
}

export async function enqueueUpdateDraftIntent(input: UpdateDraftIntentInput): Promise<OfflineUpdateDraftIntent> {
  return createQueuedIntent<OfflineUpdateDraftIntent>({
    entityType: "requisition",
    intentType: "update_draft",
    localDraftId: input.localDraftId,
    serverRequisitionId: null,
    vesselId: input.vesselId,
    authorUserId: input.authorUserId,
    payload: input.payload,
  });
}

export async function enqueueSubmitDraftIntent(input: SubmitDraftIntentInput): Promise<OfflineSubmitDraftIntent> {
  return createQueuedIntent<OfflineSubmitDraftIntent>({
    entityType: "requisition",
    intentType: "submit_draft",
    localDraftId: input.localDraftId,
    serverRequisitionId: null,
    vesselId: input.vesselId,
    authorUserId: input.authorUserId,
    payload: {},
  });
}

export async function enqueueCreateCommentIntent(input: CreateCommentIntentInput): Promise<OfflineCreateCommentIntent> {
  return createQueuedIntent<OfflineCreateCommentIntent>({
    entityType: "requisition",
    intentType: "create_comment",
    localDraftId: input.localDraftId,
    serverRequisitionId: input.serverRequisitionId,
    vesselId: input.vesselId,
    authorUserId: input.authorUserId,
    payload: input.payload,
  });
}

export async function listPendingRequisitionIntents(): Promise<OfflineQueueRecord[]> {
  return listQueuedIntentsByStatus("pending");
}
